import { IconCheck, IconShield } from '@developerz/ui';
import { For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { OrgSsoResponse } from '../lib/orgSso';
import { Panel } from './Panel';
import { StatusPill } from './StatusPill';
import { Button } from './ui/Button';

// Read-only summary of the connected IdP (#468) — presenter only. The masked
// coords the API returns render as mock-style "Label · value" .row-list rows;
// sealed material never reaches this component. SAML rows are seed-provisioned:
// summary + footer note here, never an edit form (and no remove control).

export type OrgSsoStatusProps = {
  readonly data: OrgSsoResponse;
  readonly busy: boolean;
  readonly onRemove: () => void;
};

type Fact = { readonly label: string; readonly value: string; readonly mono: boolean };

export function OrgSsoStatus(props: OrgSsoStatusProps): JSX.Element {
  const facts = (): readonly Fact[] => {
    const cfg = props.data.protocol === 'oidc' ? props.data.config : null;
    const rows: Fact[] = cfg
      ? [
          { label: t('account_sso.issuer_label'), value: cfg.issuer, mono: true },
          { label: t('account_sso.client_id_label'), value: cfg.client_id, mono: true },
          { label: t('account_sso.redirect_label'), value: cfg.redirect_uri, mono: true },
          {
            label: t('account_sso.gate_kind_label'),
            value: cfg.claim_gate.required_kind,
            mono: false,
          },
          {
            label: t('account_sso.gate_app_label'),
            value: cfg.claim_gate.required_oauth_app,
            mono: false,
          },
        ]
      : [];
    const url = props.data.login_url;
    if (url) rows.push({ label: t('account_sso.login_url_label'), value: url, mono: true });
    return rows;
  };

  return (
    <Panel
      title={t('account_sso.overview')}
      class="account-sso__overview"
      // exactOptionalPropertyTypes: only pass `footer` when SAML sets it
      {...(props.data.protocol === 'saml' ? { footer: t('account_sso.saml_note') } : {})}
      action={
        <Show when={props.data.protocol !== 'saml'}>
          <Button
            variant="outline"
            size="sm"
            disabled={props.busy}
            onClick={() => props.onRemove()}
          >
            {t('account_sso.remove')}
          </Button>
        </Show>
      }
    >
      <ul class="row-list">
        <li class="row-list__row">
          <div class="row-list__lead">
            <span class="row-list__tile" aria-hidden="true">
              <IconShield />
            </span>
            {/* Protocol name is API data, not copy — rendered verbatim. */}
            <p class="account-sso__protocol">{props.data.protocol?.toUpperCase()}</p>
          </div>
          <StatusPill tone={props.data.enabled ? 'success' : 'neutral'}>
            {props.data.enabled
              ? t('account_sso.status_enabled')
              : t('account_sso.status_disabled')}
          </StatusPill>
        </li>
        <For each={facts()}>
          {(f) => (
            <li class="row-list__row">
              <div class="row-list__lead">
                <span class="row-list__tile" aria-hidden="true">
                  <IconCheck />
                </span>
                <p class="account-sso__fact">
                  <span class="account-sso__fact-label">{f.label}</span>
                  <Show when={f.mono} fallback={<span>{f.value}</span>}>
                    <code class="account-sso__fact-value">{f.value}</code>
                  </Show>
                </p>
              </div>
            </li>
          )}
        </For>
      </ul>
    </Panel>
  );
}
